import {AgGridReact} from "ag-grid-react";
import React, {
    forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState,
} from "react";
import "../css/Grid.css";
import CustomHeader from "./CustomHeader";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import {Download, Settings} from "react-feather";
import Spinner from "./Spinner";
import ActionMenu from "./input/ActionMenu";
import {MODULES} from "../const";
import {addAuditRecord} from "../api/auditApi";
import useUserCompany from "../hooks/useUserCompany";
import {useSelector} from "react-redux";
import Modal from "./Modal";
import HeaderSelector from "./transaction/JobMonitor/HeaderSelector";
import {
    getAuthenticatedConfig, getCustomConfig, getDefaultConfig,
} from "./grid/grid-helper";
import {formatCsvDate} from "../dateConfig";

const CustomLoadingOverlay = (props) => {
    return (<div className="ag-overlay-loading-center" role="presentation">
            <div
                role="presentation"
                style={{
                    height: 100, width: 100, display: "flex", alignItems: "center", justifyContent: "center",
                }}
            >
                <Spinner size="medium" color="white"/>
            </div>
            <div aria-live="polite" aria-atomic="true">
                {props.loadingMessage}
            </div>
        </div>);
};

const Grid = forwardRef(({
                             content = [],
                             config = [],
                             style = {},
                             cellClickHandler,
                             cellFocusHandler,
                             multiRowSelection = false,
                             heading,
                             isLoading,
                             onSelectionChanged,
                             onRowDoubleClicked,
                             searchable = true,
                             rowHeight = 50,
                             onCellEditingStopped = () => {
                             },
                             onCellDoubleClicked = () => {
                             },
                             onCellMouseOver = () => {
                             },
                             gridOptions = {},
                             downloadOnlySelected = true,
                             showDownloadButton = false,
                             suppressCellSelection = false,
                             resizable = true,
                             editable = false,
                             rowDragManaged = false,
                             pagination = true,
                             pageSize = 10,
                             onRowDragEnter = () => {
                             },
                             onRowDragEnd = () => {
                             },
                             onRowDragMove = () => {
                             },
                             onRowDragLeave = () => {
                             },
                             cellSizeStrategy = "fitGridWidth",
                             fitContentCellls = [],
                             columnVirtualisation = false,
                             showActionMenu = false,
                             actionMenuOptions = [],
                             handleActionMenuChange = () => {
                             },
                             actionMenuLoading = false,
                             customActionMenu = null,
                             hasHeader = false,
                             header = <></>,
                             wrapText = true,
                             autoHeight = true,
                             stopEditingWhenCellsLoseFocus = false,
                             isRowSelectable = () => true,
                             dropdown = false,
                             dropdownValue = null,
                             dropdownOptions = {
                                 clearable: false, search: false, placeholder: "", label: "", multiple: false,
                             },
                             downloadPopUpScreen = false,
                             handleDownloadPopUpChange = () => {
                             },
                             columnDownloadSelect = false,
                             module = MODULES.Transaction,
                             showDeviceRegisterButton = false,
                             handleDeviceRegisterPopUpChange = () => {
                             },
                             deviceRegisterOpt = [],
                             defaultFilterText = "",
                             fullRowEdit = true,
                             downloadAction = "",
                             downloadParam,
                             reportName = "export_report.csv",
                             vrScroll = false,
                             gridHeight = 600,
                             storageKey = null,
                             persistColumnConfig = false,
                             persistCurrentPage = false,
                             getRowId,
                         }, ref,) => {
    const company = useUserCompany();
    const auth = useSelector((state) => state.user.permissions[module]);
    const user = useSelector((state) => state.user);

    const [popupKey, setPopupKey] = useState(1);

    const authorizedConfig = useCallback((flex) => getAuthenticatedConfig(config, auth, flex), [config, auth],);

    const buildInitial = useCallback(() => {
        if (!(persistColumnConfig && storageKey)) {
            return {grid: authorizedConfig(true)};
        }

        const saved = localStorage.getItem(storageKey);

        if (!saved) {
            return {grid: authorizedConfig(false)};
        }

        const savedConf = JSON.parse(saved);

        const customConfig = getCustomConfig(auth, authorizedConfig(false), persistColumnConfig, storageKey, false,);

        const grid = customConfig.map((con) => {
            const key = con.field ?? con.headerName;
            const w = savedConf?.[key]?.width;

            if (w != null) return {...con, width: w, flex: 0};
            return {...con, flex: 1};
        });

        return {grid};
    }, [authorizedConfig, auth, persistColumnConfig, storageKey]);

    const [{grid}] = useState(() => buildInitial());
    const [gridConfig, setGridConfig] = useState(() => grid);

    useEffect(() => {
        if (!(persistColumnConfig && storageKey)) {
            setGridConfig(authorizedConfig(true));
            return;
        }

        const saved = localStorage.getItem(storageKey);
        const savedConf = saved ? JSON.parse(saved) : {};

        const customConfig = getCustomConfig(auth, authorizedConfig(false), persistColumnConfig, storageKey, false,);

        const updated = customConfig.map((con) => {
            const key = con.field ?? con.headerName;
            const w = savedConf?.[key]?.width;
            if (w != null) return {...con, width: w, flex: 0};
            return {...con, flex: 1};
        });

        setGridConfig(updated);
    }, [authorizedConfig, auth, persistColumnConfig, storageKey]);

    useEffect(() => {
        if (defaultFilterText) {
            setFilterText(defaultFilterText);
        }
    }, [defaultFilterText]);

    const [gridApi, setGridApi] = useState(null);
    const gridRef = useRef();
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [filterText, setFilterText] = useState("");

    const modalRef = useRef(null);

    const onGridReady = (params) => {
        setGridApi(params.api);
        if (persistCurrentPage) {
            const PAGE_KEY = `${storageKey}_page`;
            const saved = localStorage.getItem(PAGE_KEY);
            console.log(saved);
            const pageIndex = saved ? Number(saved) : 0;
            const totalPages = params.api.paginationGetTotalPages?.() ?? 0;
            const safeIndex = totalPages > 0 ? Math.min(Math.max(pageIndex, 0), totalPages - 1) : 0;

            params.api.paginationGoToPage(safeIndex);
        }
    };

    const autoSizeAll = useCallback((skipHeader) => {
        if (!gridRef.current?.api) return;
        const allColumnIds = [];
        gridRef.current.api.getColumns().forEach((column) => {
            allColumnIds.push(column.getId());
        });
        gridRef.current.api.autoSizeColumns(allColumnIds, skipHeader);
    }, []);

    useImperativeHandle(ref, () => ({
        api: gridApi,
        getSelectedData: () => gridApi?.getSelectedRows?.() || [],
        getSelectedNodes: () => gridApi?.getSelectedNodes?.() || [],
        download: () => {
            const fileName = reportName;
            const params = {
                fileName,
                onlySelected: downloadOnlySelected,
                columnKeys: selectedColumns.length > 0 ? selectedColumns : undefined,
            };

            gridApi?.exportDataAsCsv?.(params);

            addAuditRecord({
                company: company ? company.comId : null,
                action: downloadAction,
                params: downloadParam,
                type: 6,
                module: 5,
                user: user.username,
            });
        },

        autoResize: autoSizeAll,
        getFilterText: () => filterText,
        evictPageCache: () => {
            const PAGE_KEY = `${storageKey}_page`;
            localStorage.removeItem(PAGE_KEY);
        },
    }));

    const loadingOverlayComponent = useMemo(() => {
        return CustomLoadingOverlay;
    }, []);

    const loadingOverlayComponentParams = useMemo(() => {
        return {
            loadingMessage: "Loading...",
        };
    }, []);

    useEffect(() => {
        if (isLoading) {
            gridRef.current?.api?.showLoadingOverlay();
        } else {
            gridRef.current?.api?.hideOverlay();
        }
    }, [isLoading]);

    const defaultColDef = useMemo(() => {
        return {
            minWidth: 200,
            padding: 5,
            cellClass: "ag-center-text-cell",
            wrapText: wrapText,
            autoHeight: wrapText && autoHeight,
            resizable: resizable,
            editable: editable,
            headerComponent: columnDownloadSelect ? CustomHeader : null,
            headerComponentParams: {
                api: gridApi, selectedColumns: selectedColumns, setSelectedColumns: setSelectedColumns,
            },
        };
    }, [gridApi, wrapText, autoHeight, resizable, editable, selectedColumns, columnDownloadSelect,]);

    let defaultGridOptions = {
        suppressCellClickSelection: true, suppressClickEdit: false, rowSelection: {


            mode: multiRowSelection ? "multiRow" : "singleRow",
            selectAll: "filtered",
            checkboxes: false,
            headerCheckbox: false,
        },
    };

    const dateComparator = (valueA, valueB) => {
        const d1 = new Date(valueA);
        const d2 = new Date(valueB);

        if (isNaN(d1) && isNaN(d2)) return 0;
        if (isNaN(d1)) return -1;
        if (isNaN(d2)) return 1;

        return d1 - d2;
    };

    if (!autoHeight) {
        defaultGridOptions = {...defaultGridOptions, rowHeight};
    }
    const currentGridOptions = {
        ...defaultGridOptions, ...gridOptions, columnTypes: {
            dateCol: {
                sortable: true, comparator: dateComparator,
            },
        },
    };

    const getSelectedColumnsData = useCallback(() => {
        if (!gridApi) return;

        const data = {};

        gridApi.forEachNode((node) => {
            selectedColumns.forEach((colId) => {
                if (!data[colId]) data[colId] = [];
                data[colId].push(node.data[colId]);
            });
        });

        return data;
    }, [gridApi, selectedColumns]);

    useEffect(() => {
        getSelectedColumnsData();
    }, [selectedColumns, getSelectedColumnsData]);

    const onColumnResized = useCallback((params) => {
        if (!params.finished || params.source !== "uiColumnResized" || !params.column) {
            return;
        }

        const col = params.column;
        const key = col.getColDef().field ?? col.getColDef().headerName;
        const width = col.getActualWidth();

        const saved = localStorage.getItem(storageKey);

        if (!saved) {
            const def = getDefaultConfig(authorizedConfig(false));
            const updated = {...def, [key]: {...(def[key] ?? {}), width}};
            localStorage.setItem(storageKey, JSON.stringify(updated));
        } else {
            const savedConfig = JSON.parse(saved);
            const updated = {
                ...savedConfig, [key]: {...(savedConfig[key] ?? {}), width},
            };
            localStorage.setItem(storageKey, JSON.stringify(updated));
        }

        params.api.refreshCells({
            force: true, columns: [col],
        });
    }, []);

    const onPaginationChanged = useCallback(() => {
        if (persistCurrentPage) {
            if (!gridApi) return;

            const PAGE_KEY = `${storageKey}_page`;

            const pageIndex = gridApi.paginationGetCurrentPage();
            console.log(pageIndex);
            localStorage.setItem(PAGE_KEY, String(pageIndex));
        }
    }, [gridApi, storageKey]);

    return (<>
            <Modal ref={modalRef} closeButton={true}>
                <HeaderSelector
                    key={popupKey}
                    storageKey={storageKey}
                    config={authorizedConfig(false)}
                    close={() => {
                        modalRef.current?.close();
                        gridRef.current?.api?.updateGridOptions({
                            columnDefs: getCustomConfig(auth, authorizedConfig(false), persistColumnConfig, storageKey, true,),
                        });
                        gridRef.current?.api?.refreshHeader();
                    }}
                />
            </Modal>
            <div
                style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "0 5px 5px 0",
                    marginTop: "5px",
                }}
            >
                {heading && <h3>{heading}</h3>}
                <div style={{display: "flex", gap: "5px"}}>
                    {persistColumnConfig && (<button
                            className="ui secondary right floated button"
                            type="button"
                            onClick={() => {
                                setPopupKey((prevState) => ++prevState);
                                modalRef.current?.showModal();
                            }}
                            style={{display: "flex", alignItems: "center"}}
                        >
                            <div style={{display: "flex", alignItems: "center"}}>
                                <div style={{display: "flex", alignItems: "center"}}>
                                    <Settings size={15}/>
                                </div>
                            </div>
                        </button>)}
                    {showDownloadButton && (<button
                            className="ui primary right floated button"
                            type="button"
                            onClick={(e) => {
                                const fileName = reportName;
                                if (downloadPopUpScreen) {
                                    handleDownloadPopUpChange();
                                } else {
                                    const params = {
                                        fileName,
                                        onlySelected: downloadOnlySelected,
                                        columnKeys: selectedColumns.length > 0 ? selectedColumns : undefined,
                                    };
                                    // gridApi?.exportDataAsCsv?.(params);
                                    const paramsWithFormatter = {
                                        ...params, processCellCallback: (cell) => {
                                            const field = cell.column.getColDef().field;
                                            let value = cell.value;

                                            const fieldConfig = {
                                                blkFrom: true,
                                                blkTo: true,
                                                lastModifiedDate: true,
                                                createdDate: true,
                                                frqRequiredDate: true,

                                                //stakeholder
                                                agrRenewal: true,
                                                agrToDate: true,
                                                agrFromDate: true,
                                                agrCreatedDate: true,
                                                stkBirthDate: true, //vehicle IN OUT
                                                plannedPickupDispatchTime: true,
                                                arrAtPickup: true,
                                                depFromPickup: true,
                                                arrivalTimeUpdate: true,
                                                departureTimeUpdate: true,
                                                ddnLastModifiedDate: true, //deduction
                                                startDate: true,
                                                endDate: true,
                                                dedReferenceStartDate: true,
                                                dedReferenceEndDate: true,
                                                warrantyEnds: true, //job monitoring
                                                gCreatedDate: true,
                                                gReqBookingApprovalDate: true,
                                                arrivalEta: true,
                                                vehicleAssignedDate: true,
                                                gFirstArrivalTime: true,
                                                gFirstDepatureTime: true,
                                                gLastArrivalTime: true,
                                                gLastDepatureTime: true,
                                                startArrivalUpdatedTime: true,
                                                startDepartureUpdatedTime: true,
                                                endArrivalUpdatedTime: true,
                                                endDepartureUpdatedTime: true,

                                            };

                                            const dateOnlyFields = ["warrantyEnds",];

                              const nicFields = ["empNic", "driverNic", "gHelNicNo", "nic", "conNic"];

                              if (
                                  (nicFields.includes(field) || (typeof value === "string" && /^\d{10,}$/.test(value))) &&
                                  value != null &&
                                  value !== ""
                              ) {
                                  return `\t${value}`;
                              }

                                            if (fieldConfig[field] && value) {
                                                if (/^\d{2}:\d{2}/.test(value)) {
                                                    return value.slice(0, 5); // → 12:30
                                                }
                                                if (dateOnlyFields.includes(field) && typeof value === "string") {
                                                    return value.split(" ")[0];
                                                }
                                                return formatCsvDate(value);
                                            }

                                            return value;
                                        },
                                    };

                                    gridApi.exportDataAsCsv(paramsWithFormatter);
                                }

                                addAuditRecord({
                                    company: company ? company.comId : null,
                                    action: downloadAction,
                                    params: downloadParam,
                                    type: 6,
                                    module: 5,
                                    user: user.username,
                                });
                            }}
                            style={{display: "flex", alignItems: "center"}}
                        >
                            <div style={{display: "flex", alignItems: "center"}}>
                                <div style={{display: "flex", alignItems: "center"}}>
                                    <Download size={15}/>
                                </div>
                            </div>
                        </button>)}
                    {showDeviceRegisterButton && (<ActionMenu
                            title="Register Devices"
                            options={deviceRegisterOpt}
                            onChangeHandler={handleDeviceRegisterPopUpChange}
                        />)}
                    {showActionMenu && gridApi && (<>
                            {customActionMenu === null ? (<ActionMenu
                                    title="Select Action"
                                    options={actionMenuOptions}
                                    onChangeHandler={handleActionMenuChange}
                                    isLoading={actionMenuLoading}
                                    dropdown={dropdown}
                                    dropdownValue={dropdownValue}
                                    dropdownOptions={dropdownOptions}
                                />) : (customActionMenu)}
                        </>)}
                </div>
            </div>
            {hasHeader && header}
            <div style={{marginTop: "5px"}}>
                {searchable && (<input
                        type="text"
                        placeholder="Search..."
                        onChange={(e) => setFilterText(e.target.value)}
                        value={filterText}
                        style={{
                            width: "100%",
                            padding: "10px",
                            marginBottom: "10px",
                            borderRadius: "5px",
                            border: "1px solid #ccc",
                            outline: "none",
                        }}
                    />)}

                <div
                    className="ag-theme-quartz ag-grid-container"
                    style={{
                        marginTop: "5px", height: vrScroll ? gridHeight : "auto", ...style,
                    }}
                >
                    <AgGridReact
                        ref={gridRef}
                        gridOptions={currentGridOptions}
                        columnDefs={gridConfig}
                        defaultColDef={defaultColDef}
                        rowData={content}
                        getRowId={getRowId}
                        domLayout={vrScroll ? "normal" : "autoHeight"}
                        pagination={pagination}
                        paginationPageSize={pageSize}
                        suppressScrollOnNewData={true}
                        animateRows={true}
                        loadingOverlayComponent={loadingOverlayComponent}
                        loadingOverlayComponentParams={loadingOverlayComponentParams}
                        onCellFocused={cellFocusHandler}
                        onCellClicked={cellClickHandler}
                        onGridReady={onGridReady}
                        onSelectionChanged={onSelectionChanged}
                        onRowDoubleClicked={onRowDoubleClicked}
                        onCellEditingStopped={onCellEditingStopped}
                        quickFilterText={filterText}
                        onCellDoubleClicked={onCellDoubleClicked}
                        onCellMouseOver={onCellMouseOver}
                        enableCellTextSelection={true}
                        suppressCellSelection={suppressCellSelection}
                        editType={fullRowEdit ? "fullRow" : ""}
                        rowDragManaged={rowDragManaged}
                        onColumnResized={onColumnResized}
                        onRowDragEnter={onRowDragEnter}
                        onRowDragEnd={onRowDragEnd}
                        onRowDragMove={onRowDragMove}
                        onRowDragLeave={onRowDragLeave}
                        stopEditingWhenCellsLoseFocus={stopEditingWhenCellsLoseFocus}
                        isRowSelectable={isRowSelectable}
                        tooltipShowDelay={500}
                        onPaginationChanged={onPaginationChanged}
                    />
                </div>
            </div>
        </>);
},);

export default Grid;
